// Organizasyon Şeması (/org-chart) — saf model fonksiyonları (spec 2026-10-15 §5, §8; görünüm katmanı spec 2026-10-24). Sayfa, kartlar ve yerleşim bu
// fonksiyonlara render olmadan da erişebilsin diye bileşenlerden ayrı tutulur.

export const ORG_NODE_TYPE = 'orgNode';
export const GROUP_NODE_TYPE = 'vacantGroup';
export const MORE_NODE_TYPE = 'moreNode';
export const TEAM_BOX_TYPE = 'teamBox';

// Aynı üst pozisyon altında aynı ünvanlı bu kadar BOŞ kardeş tek kartta toplanır (yalnız yönetilen dalda).
export const VACANT_GROUP_MIN = 3;
// Bu günden UZUN süredir boş olan koltuk uyarı rengiyle gösterilir.
export const VACANT_WARN_DAYS = 30;
// Sunucuyla aynı sınırlar (OrgChartLimits).
export const ALL_DEPTH_LIMIT = 500;
export const MAX_EXPAND = 200;
export const CHILDREN_PAGE_SIZE = 100;
export const SEARCH_MIN_LENGTH = 2;
export const SEARCH_DEBOUNCE_MS = 300;
export const SEARCH_PAGE_SIZE = 20;
export const HIGHLIGHT_MS = 3000;
export const BREADCRUMB_TAIL = 3;

export const FOCUS_ME = 'me';
export const FOCUS_ROOT = 'root';
export const VIEW_TREE = 'tree';
export const VIEW_PERSON = 'person';
// Lejantta en çok bu kadar departman; fazlası "+N" listesinde (spec 2026-10-24 §5.4).
export const LEGEND_MAX = 6;
// Kişi Odaklı yönetici zinciri: bundan uzunsa ilk 1 + "…" + son CHAIN_TAIL (spec 2026-10-24 §6).
export const CHAIN_MAX = 6;
export const CHAIN_TAIL = 4;

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const REQUISITIONS_PATH = '/ats/requisitions';

// ─────────────────────────── URL (spec 2026-10-24 §4.1) ───────────────────────────

// `depth` artık okunmaz (eski bağlantılar): sayfa onu URL'den düşürür. `edit` yalnız ağaçta anlamlıdır.
export function parseOrgChartSearch(search) {
  const params = new URLSearchParams(search || '');
  const rawFocus = (params.get('focus') || '').trim();
  let focus = FOCUS_ME;
  if (rawFocus === FOCUS_ROOT) focus = FOCUS_ROOT;
  else if (GUID.test(rawFocus)) focus = rawFocus.toLowerCase();
  // Bozuk değer olduğu gibi kalır: sunucu 404 döner, sayfa "bulunamadı" şeridiyle kendi pozisyonuna döner.
  else if (rawFocus && rawFocus !== FOCUS_ME) focus = rawFocus;
  const view = (params.get('view') || '').trim().toLowerCase() === VIEW_PERSON ? VIEW_PERSON : VIEW_TREE;
  return { view, focus, edit: view === VIEW_TREE && params.get('edit') === '1' };
}

export function buildOrgChartSearch({ view = VIEW_TREE, focus = FOCUS_ME, edit = false } = {}) {
  const params = new URLSearchParams();
  params.set('view', view === VIEW_PERSON ? VIEW_PERSON : VIEW_TREE);
  params.set('focus', focus || FOCUS_ME);
  if (edit && view !== VIEW_PERSON) params.set('edit', '1');
  return `?${params.toString()}`;
}

// ─────────────────────────── Graf ───────────────────────────
//
// Graph = { byId, children: { parentId: [childId] } (sunucu sırası), parentOf, loaded: { parentId: true },
//           totals: { parentId: totalCount }, pages: { parentId: page }, focusId, parentId (odağın üstü), rootIds }

export function graphFromView(view) {
  const byId = {};
  (view?.nodes || []).forEach((n) => { byId[n.id] = n; });
  const children = {};
  const parentOf = {};
  (view?.edges || []).forEach((e) => {
    if (!byId[e.source] || !byId[e.target] || parentOf[e.target]) return;
    if (!children[e.source]) children[e.source] = [];
    children[e.source].push(e.target);
    parentOf[e.target] = e.source;
  });
  const loaded = {};
  (view?.loadedParentIds || []).forEach((id) => { loaded[String(id)] = true; });
  const focusId = view?.focusId ? String(view.focusId) : null;
  const parentId = focusId ? (parentOf[focusId] || null) : null;
  const rootIds = focusId
    ? [parentId || focusId]
    : (view?.nodes || []).filter((n) => !parentOf[n.id]).map((n) => n.id);
  return { byId, children, parentOf, loaded, totals: {}, pages: {}, focusId, parentId, rootIds };
}

// /children yanıtını grafa katar. 1. sayfa üstün çocuk sırasını sunucudan baştan kurar (odak dahil); sonraki sayfalar ekler.
// Zaten BAŞKA bir üste bağlı düğüm (bozuk veride döngü) bu üste kenar almaz.
export function mergeChildren(graph, parentId, payload, page = 1) {
  const byId = { ...graph.byId };
  const parentOf = { ...graph.parentOf };
  const incoming = (payload?.nodes || []).map((n) => n.id);
  (payload?.nodes || []).forEach((n) => {
    if (!byId[n.id]) byId[n.id] = n;
    if (!parentOf[n.id] && !graph.rootIds.includes(n.id)) parentOf[n.id] = parentId;
  });
  const base = page > 1 ? (graph.children[parentId] || []) : [];
  const ids = base.concat(incoming.filter((id) => !base.includes(id) && parentOf[id] === parentId));
  // 1. sayfa sunucudan baştan kurulur AMA zaten bu üste bağlı olup yanıtta olmayan çocuklar (reveal'in zorla eklediği yol
  // halkası, bütçeye takılmış yanıttan kalanlar) düşmez — yoksa seçili kart üstsüz kalırdı.
  if (page === 1) {
    (graph.children[parentId] || []).forEach((id) => { if (!ids.includes(id) && parentOf[id] === parentId) ids.push(id); });
  }
  if (parentId === graph.parentId && graph.focusId && !ids.includes(graph.focusId)) ids.push(graph.focusId);
  return {
    ...graph,
    byId,
    parentOf,
    children: { ...graph.children, [parentId]: ids },
    loaded: { ...graph.loaded, [parentId]: true },
    totals: { ...graph.totals, [parentId]: payload?.totalCount ?? graph.totals[parentId] },
    pages: { ...graph.pages, [parentId]: page },
  };
}

export function remainingChildren(graph, parentId) {
  const total = graph.totals[parentId] ?? graph.byId[parentId]?.data?.directCount ?? 0;
  return Math.max(0, total - (graph.children[parentId] || []).length);
}

// Çizilecek ağaç: kapalı düğümün alt dalı gizli; yüklenmiş ama eksik çocuklu üstün sonuna "+N Daha Göster" düğümü.
export function visibleTree(graph, { collapsed = new Set() } = {}) {
  const nodes = [];
  const edges = [];
  const seen = new Set();
  const walk = (id) => {
    const current = graph.byId[id];
    if (!current || seen.has(id)) return;
    seen.add(id);
    nodes.push(current);
    if (collapsed.has(id)) return;
    const kids = graph.children[id] || [];
    kids.forEach((kid) => {
      if (!graph.byId[kid] || seen.has(kid)) return;
      edges.push({ id: `e-${id}-${kid}`, source: id, target: kid });
      walk(kid);
    });
    const remaining = graph.loaded[id] ? remainingChildren(graph, id) : 0;
    if (remaining > 0) {
      const moreId = `more:${id}`;
      nodes.push({ id: moreId, type: MORE_NODE_TYPE, position: { x: 0, y: 0 }, data: { parentId: id, remaining } });
      edges.push({ id: `e-${id}-${moreId}`, source: id, target: moreId });
    }
  };
  graph.rootIds.forEach(walk);
  return { nodes, edges };
}

// Mutasyon sonrası yenileme (spec §5.9): açık (yüklü ve kapalı olmayan) düğümler.
export function expandIdsForRefresh(graph, collapsed, extra = []) {
  const ids = Object.keys(graph.loaded).filter((id) => graph.loaded[id] && !collapsed.has(id));
  extra.forEach((id) => {
    const key = id == null ? null : String(id);
    if (key && !ids.includes(key)) ids.push(key);
  });
  return ids.slice(0, MAX_EXPAND);
}

// Kökten düğüme (düğüm dahil) — `parentOf` zinciri; bozuk veride döngüye karşı ziyaret kümesi.
export function pathTo(graph, id) {
  if (!id || !graph?.byId?.[id]) return [];
  const path = [];
  const seen = new Set();
  let current = id;
  while (current && !seen.has(current)) {
    seen.add(current);
    path.unshift(current);
    current = graph.parentOf[current];
  }
  return path;
}

/**
 * "Alt Dalı Tamamen Aç" (spec 2026-10-24 §7.1): `view?focus=<id>&depth=all` yanıtını grafa katar. Yalnız yanıtın
 * `loadedParentIds`'indeki üstlerin çocuk listesi yeniden kurulur — odağın üstü yanıtta yalnız `[odak]` çocuğuyla gelir ve
 * listede yoktur, onun kardeşleri EZİLMEZ. Var olan düğüm verisi korunur.
 */
export function mergeSubtree(graph, view) {
  const byId = { ...graph.byId };
  const parentOf = { ...graph.parentOf };
  const children = { ...graph.children };
  const loaded = { ...graph.loaded };
  (view?.nodes || []).forEach((n) => { if (!byId[n.id]) byId[n.id] = n; });
  const parents = new Set((view?.loadedParentIds || []).map(String));
  // Var olan çocuklar (2. sayfa, reveal'in zorla eklediği yol halkası) korunur; yanıtın sırası öne alınır. Böylece
  // "Daha Göster" sayımı (totals/pages) tutarlı kalır.
  const previous = {};
  parents.forEach((p) => { previous[p] = graph.children[p] || []; children[p] = []; loaded[p] = true; });
  (view?.edges || []).forEach((e) => {
    if (!parents.has(e.source) || !byId[e.target]) return;
    if (parentOf[e.target] && parentOf[e.target] !== e.source) return;   // döngü kenarı yazılmaz
    if (!parentOf[e.target] && graph.rootIds.includes(e.target)) return;
    parentOf[e.target] = e.source;
    if (!children[e.source].includes(e.target)) children[e.source].push(e.target);
  });
  parents.forEach((p) => {
    previous[p].forEach((id) => { if (!children[p].includes(id) && parentOf[id] === p) children[p].push(id); });
  });
  return { ...graph, byId, parentOf, children, loaded };
}

// "Hepsini Daralt": kökler dışındaki bütün yüklü üstler kapanır (kökler + 1. seviye açık kalır).
export function collapseAll(graph) {
  return new Set(Object.keys(graph.loaded).filter((id) => graph.loaded[id] && !graph.rootIds.includes(id)));
}

// ─────────────────────────── Departman rengi ve lejant (spec 2026-10-24 §5.4) ───────────────────────────

export function departmentColor(departmentId, palette) {
  if (!departmentId) return palette.neutral;
  const hash = [...String(departmentId)].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);
  return palette.colors[hash % palette.colors.length];
}

export function legendDepartments(nodes, max = LEGEND_MAX) {
  const byDept = new Map();
  (nodes || []).forEach((n) => {
    if (n.type && n.type !== ORG_NODE_TYPE) return;
    const d = n.data || {};
    if (!d.departmentId) return;
    const key = String(d.departmentId);
    const entry = byDept.get(key) || { id: key, name: d.departmentName || '-', count: 0 };
    entry.count += 1;
    byDept.set(key, entry);
  });
  const all = [...byDept.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'tr'));
  return { shown: all.slice(0, max), hidden: all.slice(max), rest: Math.max(0, all.length - max) };
}

// Kişi Odaklı yönetici zinciri (spec 2026-10-24 §6).
export function chainForDisplay(crumbs = []) {
  if (crumbs.length <= CHAIN_MAX) return { head: crumbs, hidden: [], tail: [] };
  return { head: crumbs.slice(0, 1), hidden: crumbs.slice(1, -CHAIN_TAIL), tail: crumbs.slice(-CHAIN_TAIL) };
}

// ─────────────────────────── Kart kararları ───────────────────────────

export function cardVariant(data, editMode) {
  if (editMode) return 'edit';
  if (data?.isOwn || data?.inManagedScope) return 'scope';
  return 'view';
}

/**
 * "Yeni Pozisyon İçin Talep" bu kartta gösterilir mi? AT modülü aktif VE kart çağıranın kendi pozisyonu ya da yönetilen
 * alt dalındaki DOLU bir pozisyon. Dal dışında ASLA — oraya talep açma yetkisi yok (sunucu da reddeder).
 */
export function canRequestNewPositionUnder(data, hasAtModule) {
  if (!hasAtModule || !data) return false;
  if (data.isOwn) return true;
  if (!data.inManagedScope) return false;
  return Boolean(data.isAssigned);
}

/**
 * Aynı üst pozisyonun altında aynı ünvanlı ≥ VACANT_GROUP_MIN boş kardeşi tek grup kartında toplar. Gruplanabilir koltuk:
 * yönetilen dalda, boş, kendi pozisyonu/odak değil, altında pozisyon YOK (yüklenmemiş olsa bile — directCount; alt dalı
 * olan koltuk gruba katılsaydı dalı şemadan kaybolurdu) ve bir üst pozisyonu var.
 * `expandedGroups` içindeki gruplar açık kalır: üyeleri tek tek çizilir, `data.groupKey` taşır.
 */
export function groupVacantSiblings(nodes, edges, expandedGroups = new Set(), keepIds = new Set()) {
  const parentOf = {};
  const hasChildren = new Set();
  edges.forEach((e) => {
    parentOf[e.target] = e.source;
    hasChildren.add(e.source);
  });

  const buckets = new Map();
  nodes.forEach((n) => {
    if (n.type && n.type !== ORG_NODE_TYPE) return;
    const d = n.data || {};
    // keepIds: seçili / vurgulu kart gruba girmez — arama ya da panelden seçilen boş koltuk grup kartında kaybolmasın.
    if (d.isAssigned || d.isOwn || d.isFocus || !d.inManagedScope || keepIds.has(n.id)) return;
    if (hasChildren.has(n.id) || (d.directCount || 0) > 0) return;
    const parent = parentOf[n.id];
    if (!parent) return;
    const titleKey = d.titleId || d.titleName;
    if (!titleKey) return;
    const key = `${parent}::${titleKey}`;
    if (!buckets.has(key)) buckets.set(key, { parent, members: [] });
    buckets.get(key).members.push(n);
  });

  const hidden = new Set();
  const expandedMember = {};
  const groupNodes = [];
  const groupEdges = [];

  buckets.forEach(({ parent, members }, key) => {
    if (members.length < VACANT_GROUP_MIN) return;
    const groupId = `vacant-group:${key}`;
    if (expandedGroups.has(groupId)) {
      members.forEach((m) => { expandedMember[m.id] = groupId; });
      return;
    }
    members.forEach((m) => hidden.add(m.id));
    const first = members[0].data || {};
    const days = members.map((m) => m.data?.vacantSinceDays).filter((v) => typeof v === 'number');
    groupNodes.push({
      id: groupId,
      type: GROUP_NODE_TYPE,
      position: { x: 0, y: 0 },
      data: {
        groupKey: groupId,
        // PDF dışa aktarma: sunucu grubun sayısını ve ünvanını bu üyelerden, görünürlük kuralıyla kendisi çıkarır.
        memberIds: members.map((m) => String(m.data?.positionId ?? m.id)),
        titleName: first.titleName || null,
        departmentName: first.departmentName || null,
        count: members.length,
        openRequisitionCount: members.filter((m) => m.data?.openRequisitionId).length,
        requestableCount: members.filter((m) => m.data?.canRequestHire).length,
        longestVacantDays: days.length ? Math.max(...days) : null,
      },
    });
    groupEdges.push({ id: `e-${parent}-${groupId}`, source: parent, target: groupId });
  });

  const outNodes = nodes
    .filter((n) => !hidden.has(n.id))
    .map((n) => (expandedMember[n.id] ? { ...n, data: { ...n.data, groupKey: expandedMember[n.id] } } : n))
    .concat(groupNodes);
  const outEdges = edges.filter((e) => !hidden.has(e.target)).concat(groupEdges);
  return { nodes: outNodes, edges: outEdges };
}

// Açık talebe gidiş. Kadro listesinin URL'den tek kayıt açma deseni YOK; `id` eklemek sessizce yok sayılırdı.
export function openRequisitionUrl() {
  return REQUISITIONS_PATH;
}

// Whole-branch review Fix 1 (Critical): builds the PUT payload for a position from its
// current org-chart node data, only overriding the fields the caller explicitly passes
// (e.g. { parentPositionId: null } for detach). Before this fix plannedHeadcount was never
// read from nodeData at all, so every drag/detach/re-parent PUT omitted the key — and
// PositionDataAccess.Update does an unconditional full-replace, so the stored value was
// silently wiped (C# cannot tell "explicit null" from "absent key").
//
// `?? undefined` (NOT `|| undefined`): plannedHeadcount is numeric and 0 is a meaningful,
// valid value ("position budgeted for zero headcount"), unlike code/departmentId/titleId/
// parentPositionId above which are IDs/strings where falsy really does mean "unset". Using
// `||` here would turn a real 0 into undefined, wiping it on every round-trip — the exact
// bug this fix closes, just for one more value.
//
// Bütünsel inceleme C-2 (Critical): `options.omitCode` KOPYALAMA (POST) yolu içindir.
// Pozisyon kodu artık kiracı içinde BENZERSİZ ve migration sonrası her pozisyonun kodu
// DOLU; kaynağın kodunu POST etmek `ResolveCode`'da daima çakışır ve kopyalama %100
// `409 position.codeExists` verirdi. Kodu hiç göndermeyince sunucu `POS-0001` biçiminde
// yeni ve benzersiz bir kod üretir (PositionDataAccess.ResolveCode).
//
// Güncelleme (PUT) yolu bu seçeneği KULLANMAZ: orada kodun düşürülmesi, pozisyonun
// mevcut kodunun her sürükleme/ayırma işleminde yeniden üretilmesi demek olurdu.
export function buildPositionPayload(nodeData, overrides, options) {
  const d = nodeData || {};
  return {
    code: options?.omitCode ? undefined : (d.code || undefined),
    plannedHeadcount: d.plannedHeadcount ?? undefined,
    departmentId: d.departmentId || undefined,
    titleId: d.titleId || undefined,
    parentPositionId: d.parentPositionId || undefined,
    ...overrides,
  };
}

// ─────────────────────────── Kırıntı ve arama yolu (spec §5.3, §5.8) ───────────────────────────

export function breadcrumbParts(crumbs = []) {
  if (crumbs.length <= BREADCRUMB_TAIL + 1) return { head: crumbs, hidden: [], tail: [] };
  return {
    head: crumbs.slice(0, 1),
    hidden: crumbs.slice(1, -BREADCRUMB_TAIL),
    tail: crumbs.slice(-BREADCRUMB_TAIL),
  };
}

export function crumbLabel(crumb) {
  return [crumb?.titleName, crumb?.userName].filter(Boolean).join(' · ') || '-';
}

export function searchPathLabel(path = []) {
  const labels = path.map((p) => p.titleName || p.userName || '-');
  if (labels.length <= 3) return labels.join(' › ');
  return `… › ${labels.slice(-3).join(' › ')}`;
}
